import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import useAuth from "@/hooks/useAuth";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { groupPortfoliosByType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { validateKeywordsSize, MAX_KEYWORDS_COUNT, MAX_KEYWORDS_TOTAL_CHARS } from "@/pages/PortfolioBuilder/keywordLimits";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import * as Sentry from "@sentry/react";
import { derivedViewColor, nextViewColor } from "@/shared/utils/savedViewHighlight";
import {
  clearPrebuiltCombinedColor,
  getPrebuiltCombinedColorsSnapshot,
  setPrebuiltCombinedColor,
  subscribeToPrebuiltCombinedColors,
} from "@/pages/PortfolioBuilder/prebuiltCombinedOverrides";

import { toCategories, type Category } from "./tagCategories";

/**
 * Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the form
 * without entering edit mode; saving still creates a new filter.
 *
 * A concierge filter is a UNION across axes (tags OR watchlists), so the
 * watchlist ids matter as much as the tags — carrying only `tags` silently
 * halves the filter's reach.
 */
/** Same members, order aside. */
const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

export interface FilterDraft {
  name?: string;
  searchKeyword?: string;
  tags?: string[];
  /** Ticker/keyword watchlists this filter matches against. */
  portfolioIds?: number[];
  /** Tag watchlists this filter matches against. */
  tagsWatchlistIds?: number[];
  filterMode?: "AND" | "OR";
  keywordOp?: "AND" | "OR";
  /** Typed keyword vs keyword-watchlist terms. Needed for an exact copy of a
   *  library filter; everything else leaves it at the default. */
  keywordWatchlistOp?: "AND" | "OR";
  /** Name of the Newsquawk combination this was opened from in the library —
   *  the builder then says so rather than "New". */
  basedOn?: string;
  /** That combination itself, so its colour can be kept for this user
   *  without copying it. See libraryChanges. */
  libraryId?: number;
  /** The colour the user currently sees it highlight in. */
  color?: string;
}

export interface UseFilterBuilderOptions {
  queryId?: number | null;
  draft?: FilterDraft | null;
  /**
   * Lists to build against instead of the signed-in user's own.
   *
   * Only the guided demo passes this. It opens this editor on a recorded plan
   * for a logged-out visitor, whose usePortfoliosCRUD is empty — so the lists
   * the draft selects would have no rows to sit in and the left pane would
   * come up blank next to a query referring to them. Same override PlanPreview
   * takes, and for the same reason.
   */
  portfoliosOverride?: Portfolio[];
  /** `id` is absent only for the guided demo, which writes nothing. */
  onSaved?: (queryName: string, categories: string[], id?: number) => void;
  /**
   * Desk fields from a host form that shows its own — the watchlist builder
   * pane, where combining is one way of making the watchlist it names. They
   * win over this hook's own, which are the full-screen builder's.
   */
  desk?: {
    publishToLibrary: boolean;
    description: string;
    whenToUse: string;
    categories: string[];
  };
  onSaveFailed?: () => void;
  onDeleted?: () => void;
}

export function useFilterBuilder(options: UseFilterBuilderOptions) {
  const confirm = useConfirm();
  const { authenticated } = useAuth();
  const { queriesQuery, createMutation, editMutation, deleteMutation } = useHeadlineQueriesCRUD();
  const { portfolios: ownPortfolios } = usePortfoliosCRUD();
  // The hook still runs unconditionally; the override just wins. Logged out it
  // resolves to an empty list anyway, so this costs nothing in the demo case.
  const portfolios = options.portfoliosOverride ?? ownPortfolios;
  const { watchlistsQuery: tagsWatchlistsQuery } = useTagsWatchlistsCRUD();
  const tagsWatchlists = tagsWatchlistsQuery.data || [];
  const headlineQueries = queriesQuery.data;

  // ── Form state ────────────────────────────────────────────────────────
  const [queryName, setQueryName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTagsWatchlistIds, setSelectedTagsWatchlistIds] = useState<number[]>([]);
  const [filterMode, setFilterMode] = useState<"AND" | "OR">("OR");
  const [keywordOp, setKeywordOp] = useState<"AND" | "OR">("OR");
  const [keywordWatchlistOp, setKeywordWatchlistOp] = useState<"AND" | "OR">("OR");
  const [notifications, setNotifications] = useState<FilterNotifications>(emptyNotifications());
  /**
   * Highlight colour the user picked. Null means "not picked" — see
   * displayColor for what stands in, and what gets saved, until they do.
   */
  const [color, setColor] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Desk fields, for when this builder is the whole form: the full-screen
   * builder, which is also where a desk user's pencil on a Newsquawk
   * combination opens. Inert for everyone else — the UI is gated on
   * `useIsDeskUser()`.
   *
   * Seeded from the record being edited, as useWatchlistCreator's are. The
   * notes ride along on every save, so an edit that never loaded them sent
   * them back empty and cleared the desk's description and when-to-use.
   *
   * `publishToLibrary` is create-only: the server ignores owner_type on PATCH.
   */
  const [publishToLibrary, setPublishToLibrary] = useState(false);
  const [description, setDescription] = useState("");
  const [whenToUse, setWhenToUse] = useState("");
  const [assetClasses, setAssetClasses] = useState<string[]>([]);

  const isEditing = options.queryId != null;
  const isNewsquawkOwned =
    isEditing &&
    headlineQueries?.find((q) => q.id === options.queryId)?.owner_type ===
      "NEWSQUAWK";

  // ── Portfolio grouping ────────────────────────────────────────────────
  const selectedGrouped = useMemo(() => {
    const selected = portfolios.filter((p) => selectedPortfolioIds.includes(p.id));
    return groupPortfoliosByType(selected);
  }, [portfolios, selectedPortfolioIds]);

  const hasBothWatchlistTypes =
    selectedGrouped.tickerOnly.length > 0 && selectedGrouped.keywordOnly.length > 0;

  useEffect(() => {
    if (hasBothWatchlistTypes && keywordOp === "OR") {
      setKeywordOp("AND");
    }
  }, [hasBothWatchlistTypes]);

  // Resolve selected tag watchlist objects
  const selectedTagWatchlistObjects = useMemo(
    () => tagsWatchlists.filter((tw) => selectedTagsWatchlistIds.includes(tw.id)),
    [tagsWatchlists, selectedTagsWatchlistIds]
  );

  // ── Keyword validation ────────────────────────────────────────────────
  const aggregatedKeywords = useMemo(() => {
    const fromWatchlists = selectedGrouped.keywordOnly.flatMap((p) => p.keywords ?? []);
    const typed = searchKeyword.trim();
    return typed ? [...fromWatchlists, typed] : fromWatchlists;
  }, [selectedGrouped.keywordOnly, searchKeyword]);

  const keywordsSize = validateKeywordsSize(aggregatedKeywords);

  const hasKeywords = searchKeyword.trim().length > 0 || selectedGrouped.keywordOnly.length > 0;
  const hasTickerWatchlists = selectedGrouped.tickerOnly.length > 0;
  const hasTags = selectedCategories.length > 0 || selectedTagsWatchlistIds.length > 0;
  const hasWatchlistsOrTags = hasTickerWatchlists || hasTags;

  // ── Counts per type ───────────────────────────────────────────────────
  const tickerCount = selectedGrouped.tickerOnly.length;
  const keywordCount = selectedGrouped.keywordOnly.length + (searchKeyword.trim() ? 1 : 0);
  const tagCount = selectedCategories.length + selectedTagsWatchlistIds.length;

  // ── Load existing query for editing ───────────────────────────────────
  useEffect(() => {
    if (!isEditing || !headlineQueries) return;
    const q = headlineQueries.find((query) => query.id === options.queryId);
    if (!q) return;

    setQueryName(q.queryName);
    setSearchKeyword(q.searchKeyword ?? "");
    setSelectedPortfolioIds(q.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(q.tagsWatchlistIds ?? []);
    setFilterMode(q.filterMode ?? "OR");
    setKeywordOp(q.keywordOp ?? "OR");
    setKeywordWatchlistOp(q.keywordWatchlistOp ?? "OR");
    setColor(q.color ?? null);
    // Keep any extra backend sources from the API — PATCH replaces the whole map
    setNotifications(q.notifications ?? emptyNotifications());

    // Convert category IDs back to Category objects
    setSelectedCategories(toCategories(q.categories ?? []));

    setPublishToLibrary(q.owner_type === "NEWSQUAWK");
    setDescription(q.description ?? "");
    setWhenToUse(q.whenToUse ?? "");
    setAssetClasses(q.assetClasses ?? []);
  }, [isEditing, options.queryId, headlineQueries]);

  // ── Seed from a concierge draft (create mode only) ────────────────────
  useEffect(() => {
    const draft = options.draft;
    if (!draft || isEditing) return;
    setQueryName(draft.name ?? "");
    setSearchKeyword(draft.searchKeyword ?? "");
    if (draft.filterMode) setFilterMode(draft.filterMode);
    if (draft.keywordOp) setKeywordOp(draft.keywordOp);
    if (draft.keywordWatchlistOp) setKeywordWatchlistOp(draft.keywordWatchlistOp);
    setSelectedCategories(toCategories(draft.tags ?? []));
    // The other half of the union — without these a "tags [Fed] OR
    // watchlists [Fed Speakers]" filter arrives as tags only.
    setSelectedPortfolioIds(draft.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(draft.tagsWatchlistIds ?? []);
    setColor(draft.color ?? null);
  }, [options.draft, isEditing]);

  /**
   * What the picker shows until they pick.
   *
   * A new list gets the next palette colour, and is saved with it — it has no
   * id yet, so the id-derived colour it would otherwise fall back to cannot be
   * previewed. An existing list with no stored colour shows the derived one
   * it is already highlighting in, and keeps it unless changed.
   */
  const ownQueryCount =
    headlineQueries?.filter((q) => q.owner_type === "USER").length ?? 0;
  const displayColor =
    color ??
    (isEditing
      ? derivedViewColor(options.queryId ?? undefined)
      : nextViewColor(ownQueryCount));
  const colorToSave = isEditing ? color : displayColor;

  // ── Toggle helpers ────────────────────────────────────────────────────
  const togglePortfolio = useCallback((id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) => {
      const exists = prev.some((c) => c.id === cat.id);
      return exists ? prev.filter((c) => c.id !== cat.id) : [...prev, cat];
    });
  }, []);

  const toggleTagWatchlist = useCallback((id: number) => {
    setSelectedTagsWatchlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const clearAll = useCallback(() => {
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
  }, []);

  const reset = useCallback(() => {
    setQueryName("");
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setNotifications(emptyNotifications());
    setColor(null);
    setPublishToLibrary(false);
    setDescription("");
    setWhenToUse("");
    setAssetClasses([]);
    setFormErrors({ categories: "", keywords: "" });
  }, []);

  // ── Semantic summary ──────────────────────────────────────────────────
  const semanticSummary = useMemo(() => {
    const parts: string[] = [];

    // Keywords
    const kwParts: string[] = [];
    if (searchKeyword.trim()) kwParts.push(`"${searchKeyword.trim()}"`);
    selectedGrouped.keywordOnly.forEach((p) => kwParts.push(p.name));
    if (kwParts.length > 0) {
      const joiner = keywordWatchlistOp === "AND" ? " and " : " or ";
      parts.push(`containing ${kwParts.join(joiner)}`);
    }

    // Tickers
    if (selectedGrouped.tickerOnly.length > 0) {
      const names = selectedGrouped.tickerOnly.map((p) => p.name);
      parts.push(`mentioning ${names.join(" or ")} tickers`);
    }

    // Tags
    const tagParts: string[] = [];
    if (selectedCategories.length > 0) tagParts.push(...selectedCategories.map((c) => c.title));
    if (selectedTagWatchlistObjects.length > 0) tagParts.push(...selectedTagWatchlistObjects.map((tw) => tw.name));
    if (tagParts.length > 0) {
      parts.push(`tagged ${tagParts.join(" or ")}`);
    }

    if (parts.length === 0) return "No filters selected";
    const joiner = keywordOp === "AND" ? " AND " : " OR ";
    return "Headlines " + parts.join(joiner);
  }, [searchKeyword, selectedGrouped, selectedCategories, selectedTagWatchlistObjects, keywordOp, keywordWatchlistOp]);

  // ── Submit ────────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({ categories: "", keywords: "" });

  /**
   * What has changed on a Newsquawk combination opened from the library, split
   * by what saving it has to do — as useWatchlistCreator's libraryChanges.
   * Its recipe, name or alerts need a combination of the user's own; its
   * colour alone can be kept as their override of the library one.
   */
  const libraryChanges = useMemo(() => {
    const draft = options.draft;
    if (isEditing || !draft?.basedOn) return null;
    const content = !(
      queryName.trim() === (draft.name ?? "").trim() &&
      searchKeyword.trim() === (draft.searchKeyword ?? "").trim() &&
      sameMembers(
        selectedCategories.map((c) => String(c.id)),
        draft.tags ?? []
      ) &&
      sameMembers(
        selectedPortfolioIds.map(String),
        (draft.portfolioIds ?? []).map(String)
      ) &&
      sameMembers(
        selectedTagsWatchlistIds.map(String),
        (draft.tagsWatchlistIds ?? []).map(String)
      ) &&
      // Only where the copy set them; otherwise they sit at their defaults.
      (draft.filterMode === undefined || filterMode === draft.filterMode) &&
      (draft.keywordOp === undefined || keywordOp === draft.keywordOp) &&
      (draft.keywordWatchlistOp === undefined ||
        keywordWatchlistOp === draft.keywordWatchlistOp) &&
      JSON.stringify(notifications) === JSON.stringify(emptyNotifications())
    );
    const look = color !== (draft.color ?? null);
    return { content, look };
  }, [
    options.draft,
    isEditing,
    queryName,
    searchKeyword,
    selectedCategories,
    selectedPortfolioIds,
    selectedTagsWatchlistIds,
    filterMode,
    keywordOp,
    keywordWatchlistOp,
    color,
    notifications,
  ]);

  const libraryId = libraryChanges ? options.draft?.libraryId : undefined;
  const copyUnchanged =
    !!libraryChanges && !libraryChanges.content && !libraryChanges.look;
  /** Only the colour differs, on a combination it can be kept for. */
  const lookOnly =
    !!libraryChanges &&
    !libraryChanges.content &&
    libraryChanges.look &&
    libraryId != null;

  const combinedColors = useSyncExternalStore(
    subscribeToPrebuiltCombinedColors,
    getPrebuiltCombinedColorsSnapshot,
    getPrebuiltCombinedColorsSnapshot
  );
  const hasLibraryOverride = libraryId != null && combinedColors[libraryId] != null;

  /** Back to the colour the desk set; closes the builder like a save. */
  const resetLibraryLook = useCallback(() => {
    if (libraryId == null) return;
    clearPrebuiltCombinedColor(libraryId);
    toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour`);
    options.onSaved?.(queryName, [], libraryId);
  }, [libraryId, options, queryName]);

  const handleSubmit = useCallback(async () => {
    if (copyUnchanged) return;
    // Colour only: keep it for this user; nothing is created.
    if (lookOnly && libraryId != null && color) {
      setPrebuiltCombinedColor(libraryId, color);
      toast.success(`Colour saved for “${options.draft?.basedOn}”`);
      options.onSaved?.(queryName, [], libraryId);
      return;
    }
    setFormErrors({ categories: "", keywords: "" });

    if (
      selectedCategories.length === 0 &&
      selectedPortfolioIds.length === 0 &&
      selectedTagsWatchlistIds.length === 0 &&
      searchKeyword.trim() === ""
    ) {
      setFormErrors((prev) => ({
        ...prev,
        categories: "Select at least one: keywords, watchlist, or tags.",
      }));
      return;
    }

    if (hasKeywords && !keywordsSize.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: keywordsSize.error ?? "Keyword limit exceeded.",
      }));
      return;
    }

    const desk = options.desk ?? {
      publishToLibrary,
      description,
      whenToUse,
      categories: assetClasses,
    };

    const payload = {
      queryName,
      categories: selectedCategories.map((cat) => String(cat.id ?? "")),
      searchKeyword,
      portfolioIds: selectedPortfolioIds,
      tagsWatchlistIds: selectedTagsWatchlistIds,
      filterMode,
      keywordOp,
      keywordWatchlistOp,
      notifications,
      // Omitted when not picked on an edit, so the stored value is left alone.
      // A half-typed hex from the text field is not a colour.
      color:
        colorToSave && /^#[0-9a-fA-F]{6}$/.test(colorToSave)
          ? colorToSave
          : undefined,
      description: desk.description.trim() || null,
      whenToUse: desk.whenToUse.trim() || null,
      assetClasses: desk.categories,
      // Create-only; the server ignores owner_type on PATCH.
      ...(desk.publishToLibrary ? ({ owner_type: "NEWSQUAWK" } as const) : {}),
    };

    try {
      if (!authenticated) {
        /**
         * Guided demo: nothing is written.
         *
         * POST /headline-queries needs an account and would 401, so the button
         * that ends the demo's most important step failed for exactly the
         * audience it was built for. Nothing to create and nothing to clean up
         * either — the combination is already in the panels via demoWatchlist,
         * so "saving" it reports the truth about what an account would do.
         * Same shape as useWatchlistCreator's unauthenticated branch.
         */
        toast.success("Watchlist created");
        options.onSaved?.(payload.queryName, payload.categories);
        return;
      }
      let savedId: number | undefined;
      if (isEditing) {
        await editMutation.mutateAsync({ id: options.queryId as number, ...payload });
        toast.success("Combined watchlist updated");
        savedId = options.queryId as number;
      } else {
        const response = await createMutation.mutateAsync({ id: undefined, ...payload });
        toast.success("Combined watchlist created");
        savedId = Number(response.data?.id);
      }
      options.onSaved?.(payload.queryName, payload.categories, savedId);
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to save combined watchlist");
      options.onSaveFailed?.();
    }
  }, [
    queryName, selectedCategories, searchKeyword, selectedPortfolioIds,
    selectedTagsWatchlistIds, filterMode, keywordOp, keywordWatchlistOp,
    notifications, colorToSave, isEditing, options.queryId, hasKeywords, keywordsSize,
    editMutation, createMutation, options.onSaved, options.onSaveFailed,
    options.desk, authenticated, copyUnchanged, lookOnly, libraryId, color,
    publishToLibrary, description, whenToUse, assetClasses,
  ]);

  const handleDelete = useCallback(async () => {
    if (!options.queryId) return;
    const ok = await confirm({ title: "Delete combined watchlist", message: "This combined watchlist will be permanently deleted. This action cannot be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok) return;

    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(options.queryId);
      toast.success("Combined watchlist deleted");
      options.onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete combined watchlist");
    } finally {
      setIsDeleting(false);
    }
  }, [options.queryId, deleteMutation, options.onDeleted]);

  return {
    // Form state
    queryName, setQueryName,
    searchKeyword, setSearchKeyword,
    selectedPortfolioIds, setSelectedPortfolioIds,
    selectedCategories, setSelectedCategories,
    selectedTagsWatchlistIds, setSelectedTagsWatchlistIds,
    filterMode, setFilterMode,
    keywordOp, setKeywordOp,
    keywordWatchlistOp, setKeywordWatchlistOp,
    notifications, setNotifications,
    color, setColor, displayColor,

    // Desk-only (see useIsDeskUser)
    publishToLibrary, setPublishToLibrary,
    description, setDescription,
    whenToUse, setWhenToUse,
    assetClasses, setAssetClasses,
    isNewsquawkOwned,

    // Toggles
    togglePortfolio,
    toggleCategory,
    toggleTagWatchlist,
    clearAll,
    reset,

    // Derived
    isEditing,
    /** The Newsquawk combination being copied, if this is one. */
    basedOn: isEditing ? undefined : options.draft?.basedOn,
    copyUnchanged,
    /** The colour can be kept for this user — see libraryChanges. */
    libraryLookable: libraryId != null,
    /** Recipe, name or alerts differ, so saving makes a new combination. */
    libraryContentChanged: !!libraryChanges?.content,
    lookOnly,
    hasLibraryOverride,
    resetLibraryLook,
    selectedGrouped,
    selectedTagWatchlistObjects,
    semanticSummary,
    keywordsSize,
    hasKeywords,
    hasTickerWatchlists,
    hasTags,
    hasWatchlistsOrTags,
    tickerCount,
    keywordCount,
    tagCount,
    formErrors,

    // Data
    portfolios,
    tagsWatchlists,

    // Mutations
    handleSubmit,
    handleDelete,
    isSubmitting: createMutation.isPending || editMutation.isPending,
    isDeleting,

    // Keyword limits
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
  };
}

export type UseFilterBuilderReturn = ReturnType<typeof useFilterBuilder>;
